/* eslint-disable @typescript-eslint/no-explicit-any */
import { ScheduledOrderStatus } from "./scheduled-orders-filter.interface";

/**
 * Processing cycle information for a scheduled order
 */
export interface ProcessingCycle {
  Id: number;
  ProcessingId?: string;
  ScheduledOrderId: number;
  Status: string;
  ProcessingErrorCode?: number;
  CreatedUtc: Date;
  CompletedUtc?: Date;
  ScheduledOrderLastOccurrenceUtc?: Date;
  ScheduledOrderNextOccurrenceUtc?: Date;
}

/**
 * Individual validation error item
 */
export interface ValidationErrorItem {
  ScheduledOrderFailureReason: string;
  ProcessingErrorCode: number;
}

/**
 * Validation result interface for pre-processing checks
 */
export interface ValidationResult {
  Succeeded: boolean;
  ScheduledOrderId: number;
  IsInRepeatTimeWindow?: boolean;
  ErrorItems?: ValidationErrorItem[];
  UpdatedUtc: Date;
}

/**
 * Bulk operations response item
 */
export interface BulkSOActionsResponse {
  ScheduledOrderId: number;
  Failed: boolean;
  Message?: string;
}

/**
 * Main scheduled order list item interface
 * Based on QPilotScheduledOrderListItem from old QMC
 */
export interface ScheduledOrderListItem {
  // Core identification
  Id: number;

  // Customer information
  CustomerFirstName: string;
  CustomerLastName: string;
  CustomerEmail: string;

  // Processing information
  LastProcessingCycleId?: number;
  LastProcessingCycle?: ProcessingCycle;
  Status: ScheduledOrderStatus;
  ProcessingErrorCode?: number;
  ValidationChecks?: ValidationResult;

  // Timing information
  LastOccurrenceUtc?: Date;
  NextOccurrenceUtc?: Date;
  UtcOffset: number;

  // Order details
  Cycles: number;
  FrequencyType: string;
  Frequency: number;

  // Shipping information
  ShippingPostcode?: string;
  ShippingCountry?: string;
  ShippingState?: string;
  PreferredShippingRateOption?: string;

  // Payment information
  PaymentMethodName?: string;
  CurrencyIso: string;

  // Order totals
  TotalProductQuantity: number;
  Subtotal?: number;
  LifetimeValue?: number;

  // Additional properties from old component
  IsInDunning?: boolean;
  Locked?: boolean;
  LastEditableDate?: Date;
  Metadata?: any;

  // UI state for bulk operations
  IsSelected: boolean;
}

/**
 * Paged result wrapper for API responses
 */
export interface PagedResult<T> {
  Items: T[];
  TotalRecords: number;
  Page: number;
  PageSize: number;
}

/**
 * Column configuration for the data table
 */
export interface TableColumn {
  field:
    | keyof ScheduledOrderListItem
    | "Actions"
    | "Checkbox"
    | "LastProcessingCycleId"
    | "Indicators";
  header: string;
  sortable: boolean;
  width?: string;
  minWidth?: string;
  type?: "text" | "date" | "currency" | "number" | "status" | "actions";
}

/**
 * Row action configuration
 */
export interface RowAction {
  label?: string;
  icon?: string;
  command?: (item: ScheduledOrderListItem) => void;
  visible?: (item: ScheduledOrderListItem) => boolean;
  disabled?: (item: ScheduledOrderListItem) => boolean;
  separator?: boolean;
}

/**
 * Default column configuration matching old QMC
 */
export const DEFAULT_TABLE_COLUMNS: TableColumn[] = [
  {
    field: "Actions",
    header: "Actions",
    sortable: false,
    width: "120px",
    type: "actions",
  },
  { field: "Checkbox", header: "", sortable: false, width: "50px" },
  { field: "Id", header: "ID", sortable: true, width: "80px", type: "number" },
  {
    field: "CustomerFirstName",
    header: "First Name",
    sortable: true,
    width: "120px",
    type: "text",
  },
  {
    field: "CustomerLastName",
    header: "Last Name",
    sortable: true,
    width: "120px",
    type: "text",
  },
  {
    field: "CustomerEmail",
    header: "Email",
    sortable: true,
    width: "180px",
    type: "text",
  },
  {
    field: "Status",
    header: "Status",
    sortable: true,
    width: "120px",
    type: "status",
  },
  {
    field: "LastProcessingCycleId",
    header: "Last Cycle Status",
    sortable: true,
    width: "150px",
  },
  {
    field: "ValidationChecks",
    header: "Validation Checks",
    sortable: false,
    width: "140px",
  },
  {
    field: "LastOccurrenceUtc",
    header: "Last Occurrence",
    sortable: true,
    width: "150px",
    type: "date",
  },
  {
    field: "NextOccurrenceUtc",
    header: "Next Occurrence",
    sortable: true,
    width: "150px",
    type: "date",
  },
  {
    field: "Cycles",
    header: "Cycles",
    sortable: true,
    width: "80px",
    type: "number",
  },
  {
    field: "Frequency",
    header: "Frequency",
    sortable: true,
    width: "90px",
    type: "number",
  },
  {
    field: "FrequencyType",
    header: "Frequency Type",
    sortable: true,
    width: "120px",
    type: "text",
  },
  {
    field: "ShippingPostcode",
    header: "Postal Code",
    sortable: true,
    width: "120px",
    type: "text",
  },
  {
    field: "ShippingCountry",
    header: "Country",
    sortable: true,
    width: "90px",
    type: "text",
  },
  {
    field: "ShippingState",
    header: "State",
    sortable: true,
    width: "80px",
    type: "text",
  },
  {
    field: "PreferredShippingRateOption",
    header: "Shipping Option",
    sortable: true,
    width: "140px",
    type: "text",
  },
  {
    field: "PaymentMethodName",
    header: "Payment Method",
    sortable: true,
    width: "140px",
    type: "text",
  },
  {
    field: "CurrencyIso",
    header: "Currency",
    sortable: true,
    width: "80px",
    type: "text",
  },
  {
    field: "TotalProductQuantity",
    header: "Product Quantity",
    sortable: false,
    width: "140px",
    type: "number",
  },
  {
    field: "Subtotal",
    header: "Subtotal",
    sortable: true,
    width: "120px",
    type: "currency",
  },
  {
    field: "LifetimeValue",
    header: "LTV",
    sortable: true,
    width: "120px",
    type: "currency",
  },
];

/**
 * Helper type for table sorting
 */
export interface SortEvent {
  field: string;
  order: 1 | -1;
  multisortmeta?: SortMeta[];
}

export interface SortMeta {
  field: string;
  order: 1 | -1;
}

/**
 * Helper type for table pagination
 */
export interface PageEvent {
  first: number;
  rows: number;
  page: number;
  pageCount: number;
}
